import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChefHat, Flame, Trophy, Users, MapPin, ArrowRight, Play, Star, Heart } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';

// Tunisia-inspired images
const HERO_IMAGES = {
  sidiBouSaid: 'https://images.unsplash.com/photo-1698702374666-25d568d81547?w=1200&q=80',
  tunisianDoor: 'https://images.unsplash.com/photo-1590001155093-a3c66ab0c3ff?w=800&q=80',
  couscous: 'https://images.unsplash.com/photo-1585937421612-70a008356fbe?w=800&q=80',
  medina: 'https://images.unsplash.com/photo-1568909944927-5827f8af934d?w=800&q=80',
  spices: 'https://images.unsplash.com/photo-1596040033229-a9821ebd058d?w=800&q=80',
  ceramics: 'https://images.unsplash.com/photo-1586981114766-708f09a71e20?w=800&q=80',
};

const FEATURED_CHEFS = [
  { name: 'Fatma B.', region: 'Tunis', barka: 2450, avatar: null, specialty: 'Couscous' },
  { name: 'Karim S.', region: 'Sfax', barka: 1890, avatar: null, specialty: 'Poissons' },
  { name: 'Leila M.', region: 'Sousse', barka: 3200, avatar: null, specialty: 'Pâtisserie' },
];

export default function LandingPage() {
  const navigate = useNavigate();
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-[#F8F5F2] overflow-hidden">
      {/* Hero Section - Full Screen */}
      <section className="relative min-h-screen flex items-center">
        {/* Background Image with Parallax */}
        <div 
          className="absolute inset-0 z-0"
          style={{ transform: `translateY(${scrollY * 0.3}px)` }}
        >
          <img 
            src={HERO_IMAGES.sidiBouSaid}
            alt="Sidi Bou Said"
            className="w-full h-[120%] object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1E4ED8]/90 via-[#1E4ED8]/60 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#F8F5F2] via-transparent to-transparent" />
        </div>

        {/* Navigation */}
        <nav className="absolute top-0 left-0 right-0 z-50 p-6">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center">
                <span className="text-2xl">🌶️</span>
              </div>
              <span className="font-fraunces text-2xl font-bold text-white">MHARHER</span>
            </div>
            <div className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-white/80 hover:text-white transition-colors">Découvrir</a>
              <a href="#chefs" className="text-white/80 hover:text-white transition-colors">Chefs</a>
              <a href="#challenges" className="text-white/80 hover:text-white transition-colors">Défis</a>
            </div>
            <div className="flex items-center gap-3">
              <Button 
                variant="ghost" 
                className="text-white hover:bg-white/20"
                onClick={() => navigate('/auth')}
              >
                Connexion
              </Button>
              <Button 
                className="bg-white text-[#1E4ED8] hover:bg-white/90 rounded-full px-6"
                onClick={() => navigate('/auth')}
              >
                Commencer
              </Button>
            </div>
          </div>
        </nav>

        {/* Hero Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-32">
          <motion.div 
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-2xl"
          >
            <Badge className="bg-white/20 text-white border-white/30 mb-6 px-4 py-2 text-sm backdrop-blur-sm">
              <MapPin className="w-4 h-4 mr-2" />
              L'âme culinaire de la Tunisie
            </Badge>
            
            <h1 className="font-fraunces text-5xl md:text-7xl font-bold text-white leading-tight mb-6">
              Cuisine.<br />
              Partage.<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#F8F5F2] to-[#E07A5F]">
                Fierté.
              </span>
            </h1>
            
            <p className="text-xl text-white/80 mb-8 leading-relaxed max-w-lg">
              Rejoins la communauté des passionnés de cuisine tunisienne. 
              Partage tes recettes, participe aux défis, et célèbre notre patrimoine culinaire.
            </p>

            <div className="flex flex-wrap gap-4">
              <Button 
                size="lg"
                className="bg-white text-[#1E4ED8] hover:bg-white/90 rounded-full px-8 text-lg h-14 shadow-xl"
                onClick={() => navigate('/auth')}
                data-testid="hero-cta"
              >
                Rejoindre la communauté
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button 
                size="lg"
                variant="ghost"
                className="text-white border-2 border-white/30 hover:bg-white/10 rounded-full px-8 text-lg h-14"
              >
                <Play className="w-5 h-5 mr-2" />
                Voir la vidéo
              </Button>
            </div>

            {/* Stats */}
            <div className="flex gap-8 mt-12 pt-8 border-t border-white/20">
              <div>
                <p className="font-fraunces text-4xl font-bold text-white">10K+</p>
                <p className="text-white/60">Chefs actifs</p>
              </div>
              <div>
                <p className="font-fraunces text-4xl font-bold text-white">500+</p>
                <p className="text-white/60">Recettes</p>
              </div>
              <div>
                <p className="font-fraunces text-4xl font-bold text-white">24</p>
                <p className="text-white/60">Régions</p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Scroll indicator */}
        <motion.div 
          animate={{ y: [0, 10, 0] }}
          transition={{ repeat: Infinity, duration: 2 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20"
        >
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center pt-2">
            <div className="w-1.5 h-3 bg-white/50 rounded-full" />
          </div>
        </motion.div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <Badge className="bg-[#1E4ED8]/10 text-[#1E4ED8] mb-4">Fonctionnalités</Badge>
            <h2 className="font-fraunces text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Plus qu'une app, une <span className="text-[#1E4ED8]">expérience</span>
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              MHARHER réunit passion, tradition et innovation pour célébrer la cuisine tunisienne
            </p>
          </motion.div>

          {/* Bento Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Large card - Stories */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="md:col-span-2 bg-white rounded-3xl p-8 shadow-sm hover:shadow-xl transition-all group overflow-hidden relative"
            >
              <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-[#1E4ED8]/10 to-transparent rounded-full -translate-y-1/2 translate-x-1/2" />
              <div className="relative z-10">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#1E4ED8] to-[#60A5FA] flex items-center justify-center mb-6 shadow-lg">
                  <Flame className="w-7 h-7 text-white" />
                </div>
                <h3 className="font-fraunces text-2xl font-bold mb-3">Stories culinaires</h3>
                <p className="text-gray-600 mb-6 max-w-md">
                  Partage les coulisses de ta cuisine. Montre tes créations éphémères et inspire la communauté.
                </p>
                <div className="flex -space-x-3">
                  {[...Array(5)].map((_, i) => (
                    <div key={i} className="w-10 h-10 rounded-full bg-gradient-to-br from-[#E07A5F] to-[#D9381E] border-2 border-white" />
                  ))}
                  <div className="w-10 h-10 rounded-full bg-[#F8F5F2] border-2 border-white flex items-center justify-center text-sm font-medium text-gray-600">
                    +99
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Small card - BARKA */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="bg-gradient-to-br from-[#D9381E] to-[#E07A5F] rounded-3xl p-8 text-white shadow-lg"
            >
              <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center mb-6 backdrop-blur-sm">
                <Star className="w-7 h-7" />
              </div>
              <h3 className="font-fraunces text-2xl font-bold mb-3">Système BARKA</h3>
              <p className="text-white/80">
                Gagne en réputation, monte en niveau, deviens une légende de la cuisine tunisienne.
              </p>
            </motion.div>

            {/* Small card - Challenges */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="bg-[#4F6F52] rounded-3xl p-8 text-white shadow-lg"
            >
              <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center mb-6 backdrop-blur-sm">
                <Trophy className="w-7 h-7" />
              </div>
              <h3 className="font-fraunces text-2xl font-bold mb-3">Défis live</h3>
              <p className="text-white/80">
                Participe aux compétitions culinaires, montre ton talent, remporte des prix.
              </p>
            </motion.div>

            {/* Large card - Community */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
              className="md:col-span-2 bg-white rounded-3xl overflow-hidden shadow-sm hover:shadow-xl transition-all group relative"
            >
              <div className="grid md:grid-cols-2">
                <div className="p-8 flex flex-col justify-center">
                  <div className="w-14 h-14 rounded-2xl bg-[#1E4ED8]/10 flex items-center justify-center mb-6">
                    <Users className="w-7 h-7 text-[#1E4ED8]" />
                  </div>
                  <h3 className="font-fraunces text-2xl font-bold mb-3">24 régions, une passion</h3>
                  <p className="text-gray-600 mb-6">
                    Découvre les spécialités de chaque gouvernorat. Du nord au sud, chaque région a ses secrets.
                  </p>
                  <Button className="w-fit bg-[#1E4ED8] hover:bg-[#1E40AF] rounded-full">
                    Explorer la carte
                    <MapPin className="w-4 h-4 ml-2" />
                  </Button>
                </div>
                <div className="relative h-64 md:h-auto">
                  <img 
                    src={HERO_IMAGES.medina}
                    alt="Medina"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-l from-transparent to-white/50" />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Featured Chefs */}
      <section id="chefs" className="py-24 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="flex flex-col md:flex-row justify-between items-start md:items-end mb-12"
          >
            <div>
              <Badge className="bg-[#E07A5F]/10 text-[#E07A5F] mb-4">Communauté</Badge>
              <h2 className="font-fraunces text-4xl md:text-5xl font-bold text-gray-900">
                Les étoiles de MHARHER
              </h2>
            </div>
            <Button variant="ghost" className="text-[#1E4ED8] mt-4 md:mt-0">
              Voir tous les chefs
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {FEATURED_CHEFS.map((chef, index) => (
              <motion.div
                key={chef.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="group cursor-pointer"
              >
                <div className="aspect-[3/4] rounded-3xl overflow-hidden bg-gradient-to-br from-[#1E4ED8] to-[#60A5FA] relative mb-4">
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                  <div className="absolute bottom-4 left-4 right-4">
                    <Badge className="bg-white/20 text-white backdrop-blur-sm mb-2">
                      {chef.specialty}
                    </Badge>
                    <h3 className="font-fraunces text-2xl font-bold text-white">{chef.name}</h3>
                    <p className="text-white/80 flex items-center gap-1 mt-1">
                      <MapPin className="w-4 h-4" />
                      {chef.region}
                    </p>
                  </div>
                  <div className="absolute top-4 right-4 bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full text-white text-sm flex items-center gap-1">
                    <Flame className="w-4 h-4" />
                    {chef.barka}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6 relative overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src={HERO_IMAGES.spices}
            alt="Spices"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-[#1E4ED8]/90" />
        </div>
        
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="relative z-10 max-w-4xl mx-auto text-center"
        >
          <h2 className="font-fraunces text-4xl md:text-6xl font-bold text-white mb-6">
            Prêt à rejoindre<br />l'aventure ?
          </h2>
          <p className="text-xl text-white/80 mb-8 max-w-2xl mx-auto">
            Crée ton compte gratuitement et commence à partager ta passion pour la cuisine tunisienne.
          </p>
          <Button 
            size="lg"
            className="bg-white text-[#1E4ED8] hover:bg-white/90 rounded-full px-10 text-lg h-14 shadow-xl"
            onClick={() => navigate('/auth')}
          >
            Créer mon compte
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </motion.div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0F172A] text-white py-16 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center">
                  <span className="text-2xl">🌶️</span>
                </div>
                <span className="font-fraunces text-2xl font-bold">MHARHER</span>
              </div>
              <p className="text-white/60">
                L'âme culinaire de la Tunisie
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Explorer</h4>
              <ul className="space-y-2 text-white/60">
                <li><a href="#" className="hover:text-white transition-colors">Recettes</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Défis</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Régions</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Chefs</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Communauté</h4>
              <ul className="space-y-2 text-white/60">
                <li><a href="#" className="hover:text-white transition-colors">Devenir créateur</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Abonnements</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Aide</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Légal</h4>
              <ul className="space-y-2 text-white/60">
                <li><a href="#" className="hover:text-white transition-colors">Conditions d'utilisation</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Confidentialité</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Cookies</a></li>
              </ul>
            </div>
          </div>
          <div className="pt-8 border-t border-white/10 text-center text-white/40 text-sm">
            © 2026 MHARHER. Tous droits réservés. Fait avec ❤️ en Tunisie.
          </div>
        </div>
      </footer>
    </div>
  );
}
